import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime } from '@/lib/utils';
import { useTransmitterChangelog } from '../../hooks/useTransmitterChangelog';
import type { ChangelogEntry } from '../../types';

// =============================================================================
// Props
// =============================================================================

interface ChangelogTabProps {
  transmitterId: string;
  enabled: boolean;
}

// =============================================================================
// Value Formatting
// =============================================================================

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '\u2014';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') {
    if (value === '') return '\u2014';
    return value;
  }
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function safeFormatDateTime(value: string): string {
  try {
    return formatDateTime(value);
  } catch {
    return value;
  }
}

// =============================================================================
// Loading Skeleton
// =============================================================================

function ChangelogSkeleton() {
  return (
    <div className="space-y-6 py-4 pl-6 border-l-2 border-muted ml-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="relative space-y-2">
          <div className="absolute -left-[25px] top-1 h-3 w-3 rounded-full bg-muted" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-24" />
          <div className="space-y-1 pt-1">
            <Skeleton className="h-3 w-64" />
            <Skeleton className="h-3 w-48" />
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Changelog Entry
// =============================================================================

function ChangelogEntryCard({ entry }: { entry: ChangelogEntry }) {
  const userName = entry.user
    ? `${entry.user.firstname} ${entry.user.lastname}`.trim()
    : null;

  return (
    <div className="relative pl-6 pb-6">
      {/* Timeline dot */}
      <div className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full border-2 border-primary bg-background" />

      {/* Entry content */}
      <div className="rounded-lg border bg-card p-3 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-foreground">
            {safeFormatDateTime(entry.timestamp)}
          </span>
          {userName && (
            <span className="text-xs text-muted-foreground">
              by {userName}
            </span>
          )}
        </div>

        {entry.changes.length > 0 && (
          <ul className="mt-2 space-y-1">
            {entry.changes.map((changeObj, i) =>
              Object.entries(changeObj).map(([field, change]) => (
                <li key={`${i}-${field}`} className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{field}</span>
                  {': '}
                  <span className="text-red-500/80 line-through">
                    {formatValue(change.old)}
                  </span>
                  {' \u2192 '}
                  <span className="text-green-600">
                    {formatValue(change.new)}
                  </span>
                </li>
              )),
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// ChangelogTab
// =============================================================================

export function ChangelogTab({ transmitterId, enabled }: ChangelogTabProps) {
  const { data, isLoading, isError, error, refetch } = useTransmitterChangelog(
    transmitterId,
    enabled,
  );

  if (isLoading) {
    return <ChangelogSkeleton />;
  }

  if (isError) {
    return (
      <div className="py-8 text-center">
        <p className="text-red-500">
          Failed to load changelog.
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
        <Button variant="outline" className="mt-4" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <p className="text-muted-foreground text-sm py-8 text-center">
        No changes recorded
      </p>
    );
  }

  return (
    <div className="py-4 pl-3 border-l-2 border-muted ml-3">
      {data.map((entry, index) => (
        <ChangelogEntryCard key={entry._id || `${entry.timestamp}-${index}`} entry={entry} />
      ))}
    </div>
  );
}
